/**
 * What a goal will look like once the server has accepted an edit.
 *
 * The goals list writes optimistically: the checkbox ticks, the bar moves and
 * the percentage changes on the click, not on the response. That only works if
 * the client can predict the two fields the server derives rather than stores —
 * `progress_pct` and `done` — because a row that ticked but kept reading «0%»
 * for a round trip is worse than one that waited.
 *
 * :func:`goalProgressPct` is a deliberate mirror of ``goal_progress_pct`` in
 * `backend/jarvis/store/goals.py` (CONTRACTS.md §1.7). It is a prediction, not
 * a decision: the next fetch overwrites whatever it says, so the cost of the
 * two disagreeing is one frame, and the tests below are what keep even that
 * from happening.
 */

import type { ChecklistItem, GoalNode, GoalPatch } from "../types";

/** Normalize any goal to 0..100. Mirrors `store/goals.py::goal_progress_pct`. */
export function goalProgressPct(
  progressType: GoalNode["progress_type"],
  currentVal: number,
  targetVal: number,
  checklist: ChecklistItem[],
): number {
  if (progressType === "checkbox") return currentVal >= 1 ? 100 : 0;
  if (progressType === "percent") return round1(clamp(currentVal, 0, 100));
  if (progressType === "checklist") {
    if (checklist.length === 0) return 0;
    return round1((checklist.filter((item) => item.done).length / checklist.length) * 100);
  }
  if (targetVal === 0) return 0;
  return round1(clamp((currentVal / targetVal) * 100, 0, 100));
}

/**
 * The fields of `goal` an accepted `patch` would change, ready to overlay.
 *
 * Only what is being edited, plus whatever the server derives from it —
 * anything else stays with the row that came off the wire.
 */
export function previewPatch(goal: GoalNode, patch: GoalPatch): Partial<GoalNode> {
  const preview: Partial<GoalNode> = { ...patch };

  const currentVal = patch.current_val ?? goal.current_val;
  const targetVal = patch.target_val ?? goal.target_val;
  const progressType = patch.progress_type ?? goal.progress_type;
  const status = patch.status ?? goal.status;

  // A type switch is the one edit whose result the client cannot predict: the
  // server hands the goal back the numbers it had the last time it wore that
  // type (`store/goals.set_progress_type`), and guessing here would show the
  // percentage of the type being left for as long as the round trip takes.
  // The refetch that follows every write is what fills the row in.
  if (patch.progress_type !== undefined) return preview;

  if (
    patch.current_val !== undefined ||
    patch.target_val !== undefined ||
    patch.status !== undefined
  ) {
    // A goal closed by hand reads 100% whatever the counter underneath says,
    // and reopening it hands the counter back — same rule as the server's
    // `build_goal_nodes`. Without it, ticking a year goal off left the bar at
    // 0% until the next fetch, and then at 0% for good.
    preview.progress_pct =
      status === "done"
        ? 100
        : goalProgressPct(progressType, currentVal, targetVal, goal.checklist);
  }

  if (patch.status !== undefined) {
    // `done` is not `status === "done"`: a goal whose numbers already reach the
    // target reads as done whatever its status says, which is the `autoDone`
    // case the row explains. Unticking such a goal must not blank the tick,
    // because the server will not blank it either.
    const pct = preview.progress_pct ?? goal.progress_pct;
    preview.done = patch.status === "done" || pct >= 100;
  }

  return preview;
}

/** Progress logged against a goal, as the row will read once it lands. */
export function previewProgress(goal: GoalNode, value: number): Partial<GoalNode> {
  const counted = goalProgressPct(goal.progress_type, value, goal.target_val, goal.checklist);
  // A goal already closed by hand stays at 100 while its counter moves: the
  // count is a record of what was done, not a vote on whether it is finished.
  const progressPct = goal.status === "done" ? 100 : counted;
  return {
    current_val: value,
    progress_pct: progressPct,
    // A numeric goal that reaches its target is done without anybody ticking
    // anything — and one that falls back below it is not.
    done: goal.status === "done" || progressPct >= 100,
  };
}

/** A goal whose checklist has changed, with its percentage moved to match. */
export function previewChecklist(goal: GoalNode, checklist: ChecklistItem[]): Partial<GoalNode> {
  const counted = goalProgressPct(
    goal.progress_type,
    goal.current_val,
    goal.target_val,
    checklist,
  );
  const progressPct = goal.status === "done" ? 100 : counted;
  return {
    checklist,
    progress_pct: progressPct,
    done: goal.status === "done" || progressPct >= 100,
  };
}

/** A checklist step ticked or unticked. */
export function previewStep(goal: GoalNode, stepId: string, done: boolean): Partial<GoalNode> {
  return previewChecklist(
    goal,
    goal.checklist.map((item) => (item.id === stepId ? { ...item, done } : item)),
  );
}

/** A checklist step removed. */
export function previewStepRemoved(goal: GoalNode, stepId: string): Partial<GoalNode> {
  return previewChecklist(
    goal,
    goal.checklist.filter((item) => item.id !== stepId),
  );
}

function clamp(value: number, low: number, high: number): number {
  return Math.min(high, Math.max(low, value));
}

function round1(value: number): number {
  return Math.round(value * 10) / 10;
}
